import { computed, onMounted, onUnmounted, reactive } from 'vue';
import { debounce } from '@/utils/debounce';

export default function useActiveBreakpoint<T extends Record<number, object>>(
    breakpoints?: T,
    direction: 'min-width' | 'max-width' = 'min-width',
    debounceTime: number = 10,
    type: | 'once' | 'alwaysResize' = 'alwaysResize'
): {
        activeBreakpoint: ComputedRef<T[keyof T] | null | undefined>;
        windowWidth: ComputedRef<number>;
    } {
    const state = reactive<
        {
            matchedBreakpoint: number | null;
            windowWidth: number;
        }
    >({
        matchedBreakpoint: null,
        windowWidth: 0
    });

    const sortedBreakpoints = breakpoints
        ? Object.keys(breakpoints)
                .map(Number)
                .sort((a, b) => (direction === 'min-width' ? b - a : a - b))
        : [];

    const updateBreakpointsValue = (innerWidth: number) => {
        if (!breakpoints) return;

        for (const key of sortedBreakpoints) {
            if (
                (direction === 'min-width' && innerWidth >= key)
                || (direction === 'max-width' && innerWidth <= key)
            ) {
                state.matchedBreakpoint = key;
                return;
            }
        }
        state.matchedBreakpoint = null;
    };

    const resize = () => {
        if (type === 'once') return;
        window.requestAnimationFrame(() => {
            state.windowWidth = window.innerWidth;
            if (breakpoints) updateBreakpointsValue(state.windowWidth);
        });
    };
    const debouncedHandleResize = debounce(resize, debounceTime);
    onMounted(() => {
        state.windowWidth = window.innerWidth;
        if (breakpoints) updateBreakpointsValue(state.windowWidth);
        if (type === 'alwaysResize') {
            window.addEventListener('resize', debouncedHandleResize);
        }
    });

    onUnmounted(() => {
        window.removeEventListener('resize', debouncedHandleResize);
        state.matchedBreakpoint = null;
        state.windowWidth = 0;
    });

    return {
        activeBreakpoint: computed(() => state.matchedBreakpoint !== null && breakpoints
            ? breakpoints[state.matchedBreakpoint as keyof T]
            : null),
        windowWidth: computed(() => state.windowWidth)
    } as const;
}
